import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrgRow {
  org_id: string;
  name: string;
  role_key: 'owner' | 'admin' | 'member' | null;
  role_label: string | null;
  member_count: number;
}

export type SettingsOrganizationsStatus = 'loading' | 'ready' | 'unauth' | 'error';

export interface UseSettingsOrganizationsVM {
  status: SettingsOrganizationsStatus;
  rows: ReadonlyArray<OrgRow>;
  error: string | null;
  refresh: () => Promise<void>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeRoleKey(raw: string | null | undefined): OrgRow['role_key'] {
  if (raw === 'owner') return 'owner';
  if (raw === 'admin') return 'admin';
  if (raw === 'member') return 'member';
  return null;
}

function roleLabelFor(key: OrgRow['role_key']): string | null {
  if (key === 'owner') return 'Owner';
  if (key === 'admin') return 'Admin';
  if (key === 'member') return 'Member';
  return null;
}

// Raw row shape returned by the list_my_orgs RPC.
interface RawOrgRow {
  org_id: string;
  name: string;
  role_key: string | null;
  role_label: string | null;
  member_count: number | null;
}

/**
 * ViewModel for the `/settings/organizations` page.
 *
 * On mount and on every `refresh()` call:
 *  1. Sets status → 'loading'.
 *  2. Checks for an active session via `supabase.auth.getSession()`.
 *     No session → 'unauth'.
 *  3. Calls `list_my_orgs()` RPC (no args).
 *     Error → 'error' + friendly message.
 *     Success → maps rows, normalizes role_key to the union or null,
 *     normalizes member_count to 0 when null → 'ready'.
 *
 * Pattern mirrors useSettingsOverviewVM.
 */
export function useSettingsOrganizationsVM(): UseSettingsOrganizationsVM {
  const [status, setStatus] = useState<SettingsOrganizationsStatus>('loading');
  const [rows, setRows] = useState<ReadonlyArray<OrgRow>>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: { cancelled: boolean }) => {
    setStatus('loading');
    setError(null);

    const { data: sessionData } = await supabase.auth.getSession();
    if (signal?.cancelled) return;

    if (!sessionData.session) {
      setStatus('unauth');
      return;
    }

    const { data, error: rpcError } = await supabase.rpc('list_my_orgs');
    if (signal?.cancelled) return;

    if (rpcError) {
      setStatus('error');
      setError("Couldn't load your organizations. Try again.");
      return;
    }

    const rawRows = (data ?? []) as RawOrgRow[];
    const mapped: OrgRow[] = rawRows.map((r) => {
      const roleKey = normalizeRoleKey(r.role_key);
      return {
        org_id: r.org_id,
        name: r.name,
        role_key: roleKey,
        role_label: r.role_label ?? roleLabelFor(roleKey),
        member_count: r.member_count ?? 0
      };
    });

    setRows(mapped);
    setStatus('ready');
  }, []);

  // Track the latest in-flight signal so both the mount effect's cleanup and a
  // subsequent refresh() can cancel a prior load — without a guard, a load that
  // resolves after unmount (e.g. refresh() racing a navigation) would setState
  // on a dead component.
  const signalRef = useRef<{ cancelled: boolean } | null>(null);

  const run = useCallback(async () => {
    if (signalRef.current) signalRef.current.cancelled = true;
    const signal = { cancelled: false };
    signalRef.current = signal;
    await load(signal);
  }, [load]);

  useEffect(() => {
    void run();
    return () => {
      if (signalRef.current) signalRef.current.cancelled = true;
    };
  }, [run]);

  const refresh = useCallback(async () => {
    await run();
  }, [run]);

  return { status, rows, error, refresh };
}
